import { apiClient } from './client';

export interface RepRequestDto {
  id: number;
  repTenantId: number;
  repName: string | null;
  supplierTenantId: number;
  supplierName: string | null;
  status: 'PENDING' | 'APPROVED' | 'REJECTED' | 'CANCELLED';
  blocked: boolean;
  createdAt: string;
  /** 'ALL' = rep can order for any retailer; 'LIMITED' = only those in retailerIds. */
  retailerMode: 'ALL' | 'LIMITED';
  /** Retailer tenant IDs allowed when mode = 'LIMITED'. Empty for ALL. */
  retailerIds: number[];
}

export async function sendRepRequest(supplierTenantId: number): Promise<RepRequestDto> {
  const { data } = await apiClient.post<RepRequestDto>('/rep-requests', { supplierTenantId });
  return data;
}

export async function cancelRepRequest(id: number): Promise<void> {
  await apiClient.put(`/rep-requests/${id}/cancel`);
}

export async function acceptRepRequest(id: number): Promise<RepRequestDto> {
  const { data } = await apiClient.put<RepRequestDto>(`/rep-requests/${id}/accept`);
  return data;
}

export async function rejectRepRequest(id: number): Promise<RepRequestDto> {
  const { data } = await apiClient.put<RepRequestDto>(`/rep-requests/${id}/reject`);
  return data;
}

/** Rep: get all my outgoing requests (status badges on Suppliers list). */
export async function getMyRepRequests(): Promise<RepRequestDto[]> {
  const { data } = await apiClient.get<RepRequestDto[]>('/rep-requests/my');
  return data;
}

/** Wholesaler: get all incoming requests (Pending tab + All Reps). */
export async function getIncomingRepRequests(): Promise<RepRequestDto[]> {
  const { data } = await apiClient.get<RepRequestDto[]>('/rep-requests/incoming');
  return data;
}

/** Wholesaler: directly add or remove a rep (no request flow needed). */
export async function directToggleRep(repTenantId: number): Promise<RepRequestDto> {
  const { data } = await apiClient.post<RepRequestDto>('/rep-requests/direct', { repTenantId });
  return data;
}

/** Wholesaler: block a rep (persisted to DB + notifies rep). */
export async function blockRep(repTenantId: number): Promise<RepRequestDto> {
  const { data } = await apiClient.post<RepRequestDto>(`/rep-requests/${repTenantId}/block`);
  return data;
}

/** Wholesaler: unblock a rep (persisted to DB + notifies rep). */
export async function unblockRep(repTenantId: number): Promise<RepRequestDto> {
  const { data } = await apiClient.post<RepRequestDto>(`/rep-requests/${repTenantId}/unblock`);
  return data;
}

/**
 * Supplier sets the retailer scope for an APPROVED rep-request.
 * mode='ALL'     → rep can order on behalf of any retailer under this supplier.
 * mode='LIMITED' → rep can only order for the given retailerIds.
 */
export async function updateRetailerScope(
  requestId: number,
  mode: 'ALL' | 'LIMITED',
  retailerIds: number[],
): Promise<RepRequestDto> {
  const { data } = await apiClient.put<RepRequestDto>(
    `/rep-requests/${requestId}/retailer-scope`,
    { mode, retailerIds },
  );
  return data;
}
